import { useState, useEffect, useContext } from 'react';
import {
  getCurrentUser,
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  fetchUserAttributes,
} from 'aws-amplify/auth';
import { AuthContext } from '../contexts/AuthContext';

/**
 * Custom hook for authentication
 * @returns Authentication utilities and state
 */
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  const { user, setUser } = context;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Check if a user is currently authenticated
   */
  const checkUser = async () => {
    try {
      setLoading(true);
      const currentUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();

      // Transform to match our User interface
      const user = {
        username: currentUser.username,
        attributes: {
          email: attributes.email || '',
          sub: currentUser.userId,
          ...attributes,
        },
      };

      setUser(user);
      setError(null);
    } catch (err) {
      setUser(null);
      setError('No authenticated user');
    } finally {
      setLoading(false);
    }
  };

  // Check user on mount
  useEffect(() => {
    checkUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Sign in a user
   * @param username The username
   * @param password The password
   */
  const signIn = async (username: string, password: string) => {
    try {
      setLoading(true);
      const signInResult = await amplifySignIn({ username, password });

      // After successful sign in, get the current user
      const currentUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();

      // Transform to match our User interface
      const user = {
        username: currentUser.username,
        attributes: {
          email: attributes.email || '',
          sub: currentUser.userId,
          ...attributes,
        },
      };

      setUser(user);
      setError(null);
      return signInResult;
    } catch (err: any) {
      setError(err.message || 'Error signing in');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign up a new user
   * @param username The username
   * @param password The password
   * @param email The email
   */
  const signUp = async (username: string, password: string, email: string) => {
    try {
      setLoading(true);
      const signUpResult = await amplifySignUp({
        username,
        password,
        options: {
          userAttributes: {
            email,
          },
        },
      });
      setError(null);
      return signUpResult;
    } catch (err: any) {
      setError(err.message || 'Error signing up');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Confirm a sign up with a verification code
   * @param username The username
   * @param code The verification code
   */
  const confirmSignUp = async (username: string, code: string) => {
    try {
      setLoading(true);
      await amplifyConfirmSignUp({ username, confirmationCode: code });
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Error confirming sign up');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign out the current user
   */
  const signOut = async () => {
    try {
      setLoading(true);
      await signOut();
      setUser(null);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Error signing out');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    user,
    loading,
    error,
    signIn,
    signUp,
    confirmSignUp,
    signOut,
    checkUser,
  };
};
